import { useCallback, useEffect, useState } from "react";
import { foyerApi } from "../http/foyer";
import { translateKey, type Translate } from "../i18n/translate";

/**
 * The PMS that owns this premise's check-ins, or null when Foyer does
 * (`occupancyDriver.ts` on the server). Every check-in control reads it: under
 * a PMS the click asks the PMS, or the control is read-only when it cannot.
 */
export interface OccupancyDriver {
  displayName: string;
  canWrite: boolean;
}

export function useOccupancySource(premiseId: string | undefined): {
  driver: OccupancyDriver | null;
  reload: () => void;
} {
  const [driver, setDriver] = useState<OccupancyDriver | null>(null);

  const reload = useCallback(() => {
    if (!premiseId) return;
    foyerApi
      .occupancySource(premiseId)
      .then((r) => setDriver(r.driver))
      // Unknown is treated as Foyer-owned: the server still refuses a flip
      // beside a PMS, so the worst case is a refusal toast, not a second
      // trigger.
      .catch(() => setDriver(null));
  }, [premiseId]);

  useEffect(reload, [reload]);
  return { driver, reload };
}

/**
 * What staff are told when a check-in or check-out through the PMS is refused,
 * or null for an error that is not the PMS's (the caller falls back to
 * `describeError`). Codes come from `setOccupancyThroughPms`.
 */
export function pmsOccupancyRefusal(
  t: Translate,
  err: unknown,
  roomId: string,
  occupied: boolean
): string | null {
  const e = err as { code?: string; body?: { vendor?: string } } | null;
  const vendor = e?.body?.vendor || t("premises:occupancySource.thePms");
  const p = { vendor, room: roomId };
  const key = (name: string) => `premises:occupancySource.${name}`;
  const inOut = occupied ? "CheckIn" : "CheckOut";
  switch (e?.code) {
    case "occupancy_managed_by_pms":
      return translateKey(t, key("managedByPms"), p);
    case "no_stay":
      return translateKey(t, key("noStay"), p);
    case "vendor_refused":
      return translateKey(t, key(`refused${inOut}`), p);
    case "unmapped":
      return translateKey(t, key("unmapped"), p);
    case "ambiguous_room":
      return translateKey(t, key("ambiguousRoom"), p);
    case "unreachable":
      return translateKey(t, key(`unreachable${inOut}`), p);
    case "credential_unavailable":
      return translateKey(t, key("credentialUnavailable"), p);
    case "capability_unavailable":
    case "breaker_open":
      return translateKey(t, key(`unavailable${inOut}`), p);
    default:
      return null;
  }
}
